import { Card } from '@chakra-ui/react';
import {
  ContactTypes,
  IAccountRespSchema,
  IContactRespSchema,
  QuoteSettingsDefaultAddressSourceEnum,
} from '@monetize/types/app';
import { FunctionComponent as FC } from 'react';
import {
  MCard,
  MDivider,
  MFormField,
  MRadio,
  MRadioGroup,
  MStack,
  MText,
  MVStack,
} from '../../../../../components/Monetize';

import { Controller } from 'react-hook-form';
import { useAtomValue } from 'jotai';
import { useGetAccountById } from '../../../../../api/accountsService';
import { AccountSelect } from '../../../../../components/Account/AccountSelect';
import { appGlobalDataState } from '../../../../../store/global.store';
import { useQuoteContext } from '../../quoteContext';
import { getCanChangeLinkedAccount } from '../../quoteUtils';
import { QuoteContactDrawerSelect } from './QuoteContactDrawerSelect';
import QuoteContactsAddressList from './QuoteContactsAddressList';

interface QuoteContactsDrawerContactsProps {
  canUpdateContact: boolean;
  canCreateContact: boolean;
}

export const QuoteContactsDrawerContacts: FC<
  QuoteContactsDrawerContactsProps
> = ({
  canUpdateContact,
  canCreateContact,
}: QuoteContactsDrawerContactsProps) => {
  const {
    quoteContacts: {
      quoteContacts,
      addressDataForm,
      isDisabled,
      isDisabledPrimaryBilling,
      handleChangeContact,
      handleChangeLinkedAccount,
      linkedAccount,
      billingAccountId,
      handleChangeBillingAccount,
      shippingAccountId,
      handleChangeShippingAccountId,
      accountSelectItems,
    },
    quoteData: { quote },
  } = useQuoteContext();
  const canChangeLinkedAccount = getCanChangeLinkedAccount(quote!);

  const { hasCrmConfigured } = useAtomValue(appGlobalDataState);
  const addressSource = addressDataForm.watch('addressSource');
  const { data: accountDetails, isLoading: isAccountLoading } =
    useGetAccountById(quote?.accountId!, {
      enabled: !!quote?.accountId,
    });

  const handleChangeContactAndEvaluate = async (
    contact: IContactRespSchema,
    type: ContactTypes,
    isInternal: boolean,
  ) => {
    handleChangeContact(contact, type, isInternal);
  };

  const showLinkedAccountSelect = canChangeLinkedAccount || !!linkedAccount;
  return (
    <>
      <MCard variant="borderless">
        <Card.Body py="2">
          <MVStack gap={2}>
            {showLinkedAccountSelect && (
              <>
                <MFormField
                  label="Linked Account"
                  tooltip="Optional account, usually a partner or reseller or customer. Used when quoting on behalf of another party."
                >
                  <AccountSelect
                    onClearAccountValue={() => handleChangeLinkedAccount(null)}
                    value={linkedAccount}
                    onChange={(event) => {
                      handleChangeLinkedAccount(
                        event as unknown as IAccountRespSchema,
                      );
                    }}
                    disabledIds={[quote?.accountId!]}
                    w="100%"
                    isDisabled={!canChangeLinkedAccount}
                    isReadOnly={!canChangeLinkedAccount}
                    returnItem
                  />
                </MFormField>
                <MDivider />
              </>
            )}
            <QuoteContactDrawerSelect
              label="Shipping Contact"
              value={quoteContacts.primary}
              onChange={(contact) =>
                handleChangeContactAndEvaluate(contact, 'primary', false)
              }
              isDisabled={isDisabledPrimaryBilling}
              isReadOnly={isDisabledPrimaryBilling}
              canUpdateContact={canUpdateContact}
              showAddress={
                addressSource === QuoteSettingsDefaultAddressSourceEnum.CONTACT
              }
              handleChangeAccount={handleChangeShippingAccountId}
              accountId={shippingAccountId!}
              accountSelectLabel="Shipping Account"
              accountSelectItems={accountSelectItems}
              showAccountSelect={!!linkedAccount}
              isAccountSelectDisabled={!canChangeLinkedAccount}
              accountSelectTooltip="This is the account that is using the services and is used to determine taxes."
            />
            <MDivider />
            <QuoteContactDrawerSelect
              label="Billing Contact"
              value={quoteContacts.billing}
              onChange={(contact) =>
                handleChangeContactAndEvaluate(contact, 'billing', false)
              }
              isDisabled={isDisabledPrimaryBilling}
              isReadOnly={isDisabledPrimaryBilling}
              canUpdateContact={canUpdateContact}
              showAddress={
                addressSource === QuoteSettingsDefaultAddressSourceEnum.CONTACT
              }
              handleChangeAccount={handleChangeBillingAccount}
              accountId={billingAccountId!}
              accountSelectLabel="Billing Account"
              accountSelectItems={accountSelectItems}
              showAccountSelect={!!linkedAccount}
              isAccountSelectDisabled={!canChangeLinkedAccount}
              accountSelectTooltip="This is the account that will receive the invoice and handle payments. All billing contacts, addresses, and payment methods must come from this account."
            />
          </MVStack>
        </Card.Body>
      </MCard>

      {hasCrmConfigured && (
        <MCard variant="borderless">
          <Card.Body py="2">
            <MText fontSize="sm" fontWeight="600">
              Address Sources
            </MText>
            <MText my="1" fontSize="sm">
              Select the source of Primary and Billing Addresses.
            </MText>
            <Controller
              name="addressSource"
              control={addressDataForm.control}
              render={({ field }) => (
                <MRadioGroup {...field} isDisabled={isDisabled}>
                  <MStack gap={1} direction="column" color="tGray.darkPurple">
                    <MRadio
                      value={QuoteSettingsDefaultAddressSourceEnum.ACCOUNT}
                      onChange={() => {
                        addressDataForm.setValue(
                          'billingAddressId',
                          accountDetails?.billingAddress?.id,
                          { shouldDirty: true },
                        );
                        addressDataForm.setValue(
                          'shippingAddressId',
                          accountDetails?.shippingAddress?.id,
                          { shouldDirty: true },
                        );
                      }}
                      fontSize="sm"
                    >
                      Source from Account Addresses
                    </MRadio>
                    <MRadio
                      value={QuoteSettingsDefaultAddressSourceEnum.CONTACT}
                      fontSize="sm"
                      onChange={() => {
                        addressDataForm.setValue('billingAddressId', '', {
                          shouldDirty: true,
                        });
                        addressDataForm.setValue('shippingAddressId', '', {
                          shouldDirty: true,
                        });
                      }}
                    >
                      Source from Contacts
                    </MRadio>
                  </MStack>
                </MRadioGroup>
              )}
            />
          </Card.Body>
        </MCard>
      )}

      {addressSource === QuoteSettingsDefaultAddressSourceEnum.ACCOUNT && (
        <QuoteContactsAddressList
          isDisabled={isDisabled}
          billingAccountId={billingAccountId!}
          shippingAccountId={shippingAccountId!}
          addressDataForm={addressDataForm}
          addressFormat={quote?.fromCompany.addressFormat}
        />
      )}
    </>
  );
};
